import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Icon, useToast } from '@kollez/design-system';
import { useCreateDrive, useParseJd, useGroupPartnersForPicker, useOpportunities } from '@/lib/queries';
import { useAuthStore } from '@/lib/authStore';

/**
 * /tpo/drives/new (F92a) — direct port of kollez/pages/tpo/post-drive.html.
 *   2-col grid · left form (JD paste → Nexie autofill, Role, Who can apply, Tell students)
 *   right sticky aside with big eligibility number + per-branch bars + Publish/Save draft.
 *   Dussehra date-clash warning (7–12 Oct).
 */

const BR_TOTALS: Record<string, number> = { CSE: 402, IT: 188, ECE: 356, EEE: 170, MECH: 142, CIVIL: 76 };
const CHANNELS = ['WhatsApp', 'Email', 'SMS'] as const;

function computeEligible(branches: Set<string>, cgpa: number, backlogs: number, sitOut: boolean) {
  const factor = Math.max(0.05, 1 - Math.max(0, cgpa - 5) * 0.16) * (backlogs > 0 ? 1.08 : 1) * (sitOut ? 0.93 : 1);
  let total = 0;
  const byBranch: Array<{ branch: string; count: number; ofTotal: number }> = [];
  for (const b of Object.keys(BR_TOTALS)) {
    if (!branches.has(b)) continue;
    const n = Math.round(BR_TOTALS[b]! * factor);
    total += n;
    byBranch.push({ branch: b, count: n, ofTotal: BR_TOTALS[b]! });
  }
  return { total, byBranch };
}

// Deep-audit note (Page 39/N): the earlier dussehraClash() hardcoded the
// 2026-10-07..12 window and matched only for Dussehra 2026. Two problems:
// (1) it went silently dead the moment we passed 2026-10-12; the same
// festival on a different calendar year would produce no warning.
// (2) it assumed every tenant in the country observes Dussehra as the
// primary autumn holiday — a college in Kerala (Onam) or Kashmir (Eid)
// would get no warning at all, and the Dussehra window is not their break.
// The proper fix reads /holidays scoped to the tenant. Until that endpoint
// is wired here, the warning always returns false (no false positives, no
// false negatives against invented Kerala/Kashmir/2027+ tenants).
function dussehraClash(_dateISO: string): boolean {
  return false;
}

function ctcToTier(ctc: number): 0 | 1 | 2 {
  return ctc >= 900000 ? 0 : ctc >= 450000 ? 1 : 2;
}

export function PostDrivePage() {
  const nav = useNavigate();
  const toast = useToast();
  const create = useCreateDrive();
  const parse = useParseJd();

  const [jd, setJd] = useState<string>('');

  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [ctcRaw, setCtcRaw] = useState('');
  const [location, setLocation] = useState('');
  const [tier, setTier] = useState<0 | 1 | 2>(1);
  const [driveDate, setDriveDate] = useState('');
  const [branches, setBranches] = useState<Set<string>>(new Set(['CSE', 'IT', 'ECE', 'EEE']));
  const [cgpa, setCgpa] = useState(6.0);
  const [pct1012, setPct1012] = useState(60);
  const [backlogs, setBacklogs] = useState(0);
  const [sitOut, setSitOut] = useState(true);
  const [workflow, setWorkflow] = useState('std');
  const [channels, setChannels] = useState<Set<string>>(new Set(['WhatsApp', 'Email']));
  const [closesAt, setClosesAt] = useState('2026-10-10T18:00');
  const [nexieFilled, setNexieFilled] = useState(false);

  // F-103: real pickers for the three required IDs (partner, opportunity, college).
  // College comes from TPO's own user record (they're a college-scoped role);
  // partner is selected from /group/partners; opportunity is filtered by partner.
  const authUser = useAuthStore((s) => s.user);
  const collegeId = authUser?.college ?? '';
  const partnersQuery = useGroupPartnersForPicker();
  const [partnerId, setPartnerId] = useState<string>('');
  const opportunitiesQuery = useOpportunities(partnerId ? { partner: partnerId, status: 'Active' } : undefined);
  // Multi-role: a Drive hosts N Opportunities (Wipro Day-1 at GIT = Elite NTH + WILP +
  // Consulting Associate — one Drive, three roles). Track selected opportunity ids as a
  // set; the first pick becomes the primary role and drives the CTC/tier suggestion.
  const [selectedOpps, setSelectedOpps] = useState<string[]>([]);
  // If Nexie filled 'company' from a JD, pre-select the matching partner when it lands.
  useEffect(() => {
    if (!partnerId && company && partnersQuery.data?.rows.length) {
      const match = partnersQuery.data.rows.find((p) => p.name.toLowerCase().includes(company.toLowerCase().split(/\s+/)[0] ?? ''));
      if (match) setPartnerId(match._id);
    }
  }, [company, partnersQuery.data, partnerId]);
  // Reset role selection when partner changes.
  useEffect(() => { setSelectedOpps([]); }, [partnerId]);

  function toggleOpp(oppId: string) {
    setSelectedOpps((prev) => (prev.includes(oppId) ? prev.filter((x) => x !== oppId) : [...prev, oppId]));
  }

  const ctc = Number(ctcRaw.replace(/[^\d]/g, '')) || 0;
  const eligibility = useMemo(() => computeEligible(branches, cgpa, backlogs, sitOut), [branches, cgpa, backlogs, sitOut]);
  const clash = dussehraClash(driveDate);

  // Timer for "Started N ago"
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const startedS = Math.floor((now - startedAt) / 1000);
  const startedLabel = startedS < 60 ? `${startedS} s` : `${Math.floor(startedS / 60)} min ${startedS % 60} s`;

  function toggleBranch(b: string) {
    const next = new Set(branches);
    next.has(b) ? next.delete(b) : next.add(b);
    setBranches(next);
  }
  function toggleChannel(c: string) {
    const next = new Set(channels);
    next.has(c) ? next.delete(c) : next.add(c);
    setChannels(next);
  }

  // Update tier when CTC changes
  useEffect(() => {
    if (ctc > 0) setTier(ctcToTier(ctc));
  }, [ctc]);

  async function fillFromJd() {
    try {
      const res = await parse.mutateAsync(jd);
      const p = res.parsed;
      if (p.company) setCompany(p.company);
      if (p.role) setRole(p.role);
      if (p.ctcInr) { setCtcRaw(p.ctcInr.toLocaleString('en-IN')); setTier(ctcToTier(p.ctcInr)); }
      if (p.location) setLocation(p.location);
      if (typeof p.minCGPA === 'number') setCgpa(p.minCGPA);
      if (Array.isArray(p.eligibleBranches) && p.eligibleBranches.length > 0) {
        setBranches(new Set(p.eligibleBranches.map((x: string) => x.toUpperCase().replace(/B\.TECH\s*/i, '').trim())));
      }
      if (p.driveDate) setDriveDate(p.driveDate.slice(0, 10));
      setNexieFilled(true);
      toast.show({
        message: clash
          ? 'Filled from the message. The date clashes with Dussehra — pick another.'
          : 'Filled from the message.',
      });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Nexie could not parse';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  async function publish() {
    const missing: string[] = [];
    if (!company.trim()) missing.push('company');
    if (!role.trim()) missing.push('role');
    if (!ctc) missing.push('CTC');
    // F-103: real IDs required.
    if (!partnerId) missing.push('partner');
    if (selectedOpps.length === 0) missing.push('at least one role (opportunity)');
    if (!collegeId) missing.push('college (login as college TPO)');
    if (missing.length) {
      toast.show({ tone: 'warning', message: `Add the ${missing.join(', ')} first` });
      return;
    }
    if (clash) {
      toast.show({ tone: 'warning', message: 'Pick a date outside the Dussehra break (7–12 Oct).' });
      return;
    }
    try {
      // Build the multi-role payload. Each selected opportunity becomes a role; the drive
      // name reflects the count when there is more than one so the TPO can eyeball a
      // "Wipro · 3 roles" chip instead of a role title that only names one of the three.
      const roles = selectedOpps.map((opportunity) => ({ opportunity }));
      const primaryTitle = role.trim() || (opportunitiesQuery.data?.opportunities.find((o) => o._id === selectedOpps[0])?.title ?? '');
      const driveNameText = selectedOpps.length > 1
        ? `${company} · ${selectedOpps.length} roles`
        : `${company} · ${primaryTitle}`;
      const res = await create.mutateAsync({
        driveName: driveNameText,
        roles,
        partner: partnerId,
        college: collegeId,
        driveDate: new Date(driveDate).toISOString(),
        tier: String(tier) as '0' | '1' | '2',
        eligibility: {
          branches: [...branches],
          minCGPA: cgpa,
          minPct: pct1012,
          maxBacklogs: backlogs,
          sitOutPlaced: sitOut,
          graduationYears: [2026],
        },
        applicationsCloseAt: new Date(closesAt).toISOString(),
        notifyChannels: [...channels].map((c) => c.toLowerCase().replace('whatsapp', 'whatsapp')) as Array<'whatsapp' | 'email' | 'sms' | 'in_app'>,
      });
      toast.show({ message: `Published. ${eligibility.total.toLocaleString('en-IN')} students are being notified.` });
      setTimeout(() => nav(`/tpo/drives/${(res as { drive?: { _id: string } }).drive?._id ?? ''}`), 1200);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Publish failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  return (
    <div className="stack" style={{ paddingBottom: 96 }}>
      <style>{`
        .pd { display: grid; gap: 20px; grid-template-columns: minmax(0,1fr); align-items: start; }
        @media (min-width: 1100px) { .pd { grid-template-columns: minmax(0,1fr) 320px; } .pd-aside { position: sticky; top: 76px; } }
        .pd-sec { display: grid; gap: 16px; }
        .pd-num { font-family: "Newsreader", Georgia, serif; font-size: 3.25rem; line-height: 1; font-weight: 500; letter-spacing: -0.02em; }
        @media (max-width: 1099px) { .pd-aside { display: none; } }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Post a drive</h1>
          <p className="subhead">Paste the company's email or job description and we'll fill in the rest. You check it and publish.</p>
        </div>
      </div>

      <div className="pd">
        <div className="grid gap-5 min-w-0">
          {/* 1. JD paste */}
          <section className="card card-pad pd-sec">
            <div className="flex items-center justify-between gap-3">
              <h2 className="h-section">1. Start from the company's message</h2>
              {nexieFilled && (
                <span className="badge badge-outline">
                  <Icon name="sparkles" size={12} />Filled by Nexie
                </span>
              )}
            </div>
            <textarea className="textarea" rows={4} value={jd} onChange={(e) => setJd(e.target.value)} placeholder="Paste the email or JD here" />
            <div className="flex flex-wrap gap-2 items-center">
              <button className="btn btn-secondary" onClick={fillFromJd} disabled={parse.isPending || !jd.trim()}>
                {parse.isPending ? <><Icon name="rotate-cw" size={14} />Reading…</> : <><Icon name="sparkles" size={14} />{nexieFilled ? 'Fill again' : 'Fill the form from this'}</>}
              </button>
              <span className="helper">or fill it in yourself below</span>
            </div>
          </section>

          {/* 2. Role */}
          <section className="card card-pad pd-sec">
            <h2 className="h-section">2. The role</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1.5">
                <span className="label">Partner company</span>
                {/* F-103: pick the actual Partner from the group's onboarded partners */}
                <select
                  className="select"
                  value={partnerId}
                  onChange={(e) => { setPartnerId(e.target.value); const p = partnersQuery.data?.rows.find((x) => x._id === e.target.value); if (p) setCompany(p.name); }}
                >
                  <option value="">— pick a partner —</option>
                  {partnersQuery.data?.rows.map((p) => (
                    <option key={p._id} value={p._id}>{p.name}</option>
                  ))}
                </select>
                {!partnerId && (
                  <span className="helper">If you don't see the company, add them via /group/partners first.</span>
                )}
              </label>
              <div className="grid gap-1.5 sm:col-span-2">
                <span className="label">
                  Roles (openings) hosted at this drive
                  {selectedOpps.length > 0 && (
                    <span className="helper ml-2">{selectedOpps.length} selected</span>
                  )}
                </span>
                {/* Multi-role: a Wipro campus visit typically hires for Elite NTH + WILP +
                    Consulting on the same day — one Drive, three roles. Chip-select every
                    opening the partner is running for this batch. */}
                {!partnerId ? (
                  <span className="helper">Pick a partner first.</span>
                ) : (opportunitiesQuery.data?.opportunities.length ?? 0) === 0 ? (
                  <span className="helper">This partner has no Active openings yet. Ask them to post one from /company/openings/new.</span>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {opportunitiesQuery.data?.opportunities.map((o) => {
                      const selected = selectedOpps.includes(o._id);
                      return (
                        <button
                          key={o._id}
                          type="button"
                          className="tag"
                          aria-pressed={selected}
                          onClick={() => {
                            toggleOpp(o._id);
                            // First selection sets the visible role name if it's still empty
                            if (!selected && !role.trim()) setRole(o.title ?? '');
                          }}
                        >
                          {o.title ?? 'Untitled'}
                        </button>
                      );
                    })}
                  </div>
                )}
                {selectedOpps.length > 1 && (
                  <span className="helper">
                    This drive will host {selectedOpps.length} roles. Students see and apply to each role separately.
                  </span>
                )}
              </div>
              <label className="grid gap-1.5">
                <span className="label">Role title</span>
                <input className="input" value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Analyst" />
              </label>
              <label className="grid gap-1.5">
                <span className="label">CTC per year</span>
                <div className="input-group">
                  <span className="input-addon">₹</span>
                  <input className="input" inputMode="decimal" value={ctcRaw} onChange={(e) => setCtcRaw(e.target.value)} placeholder="7,60,000" />
                </div>
                <span className="helper">{ctc > 0 ? `₹${ctc.toLocaleString('en-IN')} → Day ${ctcToTier(ctc)}` : 'Sets the drive tier automatically'}</span>
              </label>
              <label className="grid gap-1.5">
                <span className="label">Location</span>
                <input className="input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder='City or "Pan India"' />
              </label>
              <div className="grid gap-1.5">
                <span className="label">Drive tier</span>
                <div className="segmented">
                  {([0, 1, 2] as const).map((t) => (
                    <button key={t} aria-pressed={tier === t} onClick={() => setTier(t)}>Day {t}</button>
                  ))}
                </div>
                <span className="helper">Day 0: ₹9 LPA+. Placed students can't sit a lower tier.</span>
              </div>
              <label className="grid gap-1.5">
                <span className="label">Drive date</span>
                <input className="input" type="date" value={driveDate} onChange={(e) => setDriveDate(e.target.value)} />
                {clash && (
                  <span className="field-error">
                    <Icon name="calendar-x" size={14} />
                    {new Date(driveDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} is in the Dussehra break (7–12 Oct).
                  </span>
                )}
              </label>
            </div>
          </section>

          {/* 3. Who can apply */}
          <section className="card card-pad pd-sec">
            <h2 className="h-section">3. Who can apply</h2>
            <div className="grid gap-1.5">
              <span className="label">Branches</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(BR_TOTALS).map(([b, n]) => (
                  <button key={b} className="tag" aria-pressed={branches.has(b)} onClick={() => toggleBranch(b)}>
                    {b} <span className="helper ml-1">{n}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <label className="grid gap-1.5">
                <span className="label">Minimum CGPA</span>
                <input className="input" type="number" step={0.1} min={0} max={10} value={cgpa} onChange={(e) => setCgpa(Number(e.target.value))} />
              </label>
              <label className="grid gap-1.5">
                <span className="label">10th and 12th</span>
                <input className="input" type="number" value={pct1012} onChange={(e) => setPct1012(Number(e.target.value))} />
                <span className="helper">Minimum %</span>
              </label>
              <label className="grid gap-1.5">
                <span className="label">Active backlogs</span>
                <select className="select" value={backlogs} onChange={(e) => setBacklogs(Number(e.target.value))}>
                  <option value={0}>None allowed</option>
                  <option value={1}>Up to 1</option>
                  <option value={2}>Up to 2</option>
                </select>
              </label>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="check mt-0.5" checked={sitOut} onChange={(e) => setSitOut(e.target.checked)} />
              <span>
                Keep out students already placed in a Day 0 or Day 1 drive
                <span className="helper block">Your placement policy, set by the group</span>
              </span>
            </label>
            <div className="grid gap-1.5">
              <span className="label">Selection process</span>
              <select className="select" value={workflow} onChange={(e) => setWorkflow(e.target.value)}>
                <option value="std">Campus drive · standard — apply, eligibility, test, interview, offer</option>
                <option value="bulk">Campus drive · IT services (bulk) — adds a group discussion</option>
                <option value="prod">Product company · work sample first</option>
                <option value="none">No process — I'll shortlist by hand</option>
              </select>
              <p className="helper m-0">
                {workflow === 'none'
                  ? "Students won't see stages or promised dates, and nothing moves automatically. Only sensible for tiny drives."
                  : "Candidates see every step and a promised date. Fewer drops between stages when the process is set."}
              </p>
            </div>
          </section>

          {/* 4. Tell students */}
          <section className="card card-pad pd-sec">
            <h2 className="h-section">4. Tell students</h2>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((c) => (
                <button key={c} className="tag" aria-pressed={channels.has(c)} onClick={() => toggleChannel(c)}>{c}</button>
              ))}
            </div>
            <label className="grid gap-1.5">
              <span className="label">Applications close</span>
              <input className="input sm:max-w-[240px]" type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} />
            </label>
          </section>
        </div>

        {/* Aside */}
        <aside className="pd-aside card">
          <div className="card-pad grid gap-1 border-b border-line-tertiary">
            <p className="eyebrow">Students who can apply</p>
            <div className="pd-num num">{eligibility.total.toLocaleString('en-IN')}</div>
            <p className="helper">
              of 1,334 in batch 2026{sitOut ? ' · placed students kept out' : ''}
            </p>
          </div>
          <div className="card-pad grid gap-2">
            {eligibility.byBranch.length === 0 ? (
              <p className="helper">Pick at least one branch.</p>
            ) : (
              eligibility.byBranch.map(({ branch, count, ofTotal }) => (
                <div key={branch}>
                  <div className="flex justify-between text-sm">
                    <span>{branch}</span>
                    <span className="num">{count.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="bar"><span style={{ width: `${Math.round((count / ofTotal) * 100)}%` }} /></div>
                </div>
              ))
            )}
          </div>
          <div className="card-foot grid gap-2">
            <button className="btn btn-primary w-full" onClick={publish} disabled={create.isPending}>
              <Icon name="send" size={14} />{create.isPending ? 'Publishing…' : 'Publish and notify'}
            </button>
            {/* Deep-audit fix (Page 39/N): 'Save draft' fired a toast
                saying 'Draft saved' with no API call. Nothing was
                persisted; a TPO refreshing after clicking would lose
                every field they'd typed. Disabled with 'soon' — Drive
                supports a 'draft' status but no per-user
                autosave-to-draft endpoint exists yet. */}
            <button
              className="btn btn-ghost w-full"
              disabled
              title="Draft autosave ships in the next release. Fill the form and Publish, or leave the tab open."
            >
              Save draft · soon
            </button>
            <p className="helper text-center">Started {startedLabel} ago</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
